import { Checkbox, Input } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { Button, Div, Flex, Icon, notification, Span, SubTitle } from 'ui';
import useLocalStorage from 'utils/hooks';
import { withRouter } from 'utils/withRouter';
import ActionButton from '../../../ui/action-button';
import { smartActionTypes } from '../../../utils/app-configs';
import useCollapseExpand from '../../../utils/hooks/useCollapseExpand';
import SmartActionCard from './smart-action-card';
import { Text } from './ticket-form-detail';

const StyledCardDiv = styled(Div)`
  .ant-card > .ant-card-actions {
    height: 50px;
  }
  .ant-card > .ant-card-actions > li {
    margin: 0 !important;
    height: 100%;
    > span {
      height: 100%;
      > i::before {
        position: relative;
        top: 25%;
      }
    }
  }
  .ant-card {
    margin-bottom: 20px !important;
  }
`;

const StyledCheckboxDiv = styled.div`
  margin: 20px 0;
`;

const StyledButton = styled(Button)`
  background-color: #5a687c !important;
  color: #fff !important;
  border-color: #5a687c !important;
  font-weight: 400;
  font-size: 13px;
  float: right;
  text-transform: uppercase;
  margin-left: 10px;
  &:hover {
    opacity: 0.7;
  }
  &:disabled {
    opacity: 0.7;
  }

  ${props =>
    props.secondary &&
    css`
      background-color: #fff !important;
      color: #5a687c !important;
    `}
`;

const SeverityIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[2]};
  margin-right: 10px;
`;

const StyledInput = styled(Input)`
  width: 100%;
  margin-bottom: 20px;
`;

const StyledDefault = styled.p`
  font-size: 16px;
  font-style: italic;
`;

const NotificationDiv = styled(Div)`
    white-space: pre-wrap;
`;

const SaveLocalNotification = ({ type, intl, handleClick }) => {
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  return (
    <NotificationDiv marginTop={10}>
      {type.information}
      <StyledCheckboxDiv>
        <Checkbox
          onChange={(e) => {
            setCheckboxChecked(e.target.checked);
          }}
        >
          {intl.formatMessage({ id: 'ticket.smart_actions.INFO_RADIO_TEXT' })}
        </Checkbox>
      </StyledCheckboxDiv>
      <StyledButton
        onClick={() => handleClick(type, checkboxChecked)}
        dataTestId='btn-add-smart-action-confirm'
      >
        {intl.formatMessage({ id: 'ticket.smart_actions.INFO_CONFIRM' })}
      </StyledButton>
    </NotificationDiv>
  );
};
SaveLocalNotification.propTypes = {
  intl: PropTypes.objects,
  type: PropTypes.object,
  handleClick: PropTypes.func,
};

const DeleteNotification = ({ action, intl, onDelete }) => {
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  return (
    <Div marginTop={10}>
      {intl.formatMessage({ id: 'ticket.smart_actions.DELETE_REASON_DESCRIPTION' })}
      <StyledInput
        placeholder={intl.formatMessage({ id: 'ticket.smart_actions.DELETE_REASON' })}
        onChange={e => setReason(e.target.value)}
      />
      <StyledButton
        onClick={() => {
          onDelete({ action, reason });
          setIsDeleting(true);
        }}
        disabled={isDeleting}
      >
        {intl.formatMessage({ id: 'prompt.DELETE' })}
      </StyledButton>
      <StyledButton secondary={true} onClick={() => notification.destroy('deleteSmartAction')}>
        {intl.formatMessage({ id: 'prompt.CANCEL' })}
      </StyledButton>
    </Div>
  );
};

DeleteNotification.propTypes = {
  intl: PropTypes.objects,
  action: PropTypes.object,
  onDelete: PropTypes.func,
};

const SmartActionSection = ({
  intl,
  history,
  actions,
  actionTypes = [],
  deleteSuccess,
  deleteFailure,
  deleteSmartAction,
  getTicketActionTypes,
  objectType,
  id,
  followUpTypes = [],
}) => {
  const [smartActionTypesInStorage, setSmartActionTypesInStorage] = useLocalStorage('types', []);
  const [arrow, content, isCollapsed] = useCollapseExpand(false);

  useEffect(() => {
    // get the ticket action types for adding smart actions
    if (objectType && id) {
      getTicketActionTypes({
        objectType,
        id,
      });
    }
  }, []);

  useEffect(() => {
    if (deleteSuccess) {
      notification.destroy('deleteSmartAction');
      notification.success({
        message: intl.formatMessage({ id: 'ticket.smart_actions.DELETION_SUCCESS' }),
        placement: 'top',
      });
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (deleteFailure) {
      notification.destroy('deleteSmartAction');
      notification.error({
        message: intl.formatMessage({ id: 'ticket.smart_actions.DELETION_FAILED' }),
        duration: 0,
        placement: 'top',
      });
    }
  }, [deleteFailure]);

  const openSidepanel = (typeId, actionId) => {
    history.push(
      // eslint-disable-next-line max-len
      `smart-action/${typeId}${actionId ? `/${actionId}` : ''}`,
      { cancelBlock: true },
    );
  };

  const handleClick = (type, checkboxChecked) => {
    if (checkboxChecked) {
      const types = [...smartActionTypesInStorage, type.id];
      setSmartActionTypesInStorage(types);
    }
    notification.destroy('addSmartAction');
    openSidepanel(type.id, null);
  };


  const addSmartAction = (type) => {
    // check if info message is already shown for the current type
    // done by getting value out of localstorage
    if (!smartActionTypesInStorage?.find(s => s === type.id)) {
      notification.info({
        key: 'addSmartAction',
        className: 'extended-notification',
        message: type.description,
        description: <SaveLocalNotification type={type} intl={intl} handleClick={handleClick} />,
        duration: 0,
        placement: 'top',
      });
    } else {
      openSidepanel(type.id, null);
    }
  };

  const deleteSmartActionNotification = (action) => {
    notification.info({
      key: 'deleteSmartAction',
      className: 'extended-notification',
      message: intl.formatMessage({
        id: 'ticket.smart_actions.DELETE_SMART_ACTION_TITLE',
      }),
      description: <DeleteNotification
        action={action}
        intl={intl}
        onDelete={deleteSmartAction}
      />,
      duration: 0,
      placement: 'top',
    });
  };

  return (
    <>
      <Div marginBottom={24}>
        <SubTitle>
          <FormattedMessage id='ticket.SMART_ACTIONS' />
          <ActionButton
            actions={actionTypes?.map(type => ({
              text: type.description,
              onClick: () => addSmartAction(type),
            }))}
            dataTestId='btn-smart-action'
          />
          {actionTypes.length ?
            <Flex marginLeftAuto ffrn aic>
              {actions?.find(a => a.hasFollowupAction) && isCollapsed ?
                <SeverityIcon type='exclamation-triangle' /> :
                null
              }
              {arrow}
            </Flex> :
            null
          }
        </SubTitle>
        {content(
          <>
            {actions?.find(a => a.hasFollowupAction) ?
              <Text>
                <SeverityIcon type='exclamation-triangle' />
                <Span marginLeft={5}>
                  {intl.formatMessage({ id: 'ticket.smart_actions.FOLLOW_UP_WARNING' })}
                </Span>
              </Text> :
              null
            }
            <StyledCardDiv className='smart-action-card'>
              {actions.length ?
                actions?.map(action =>
                  <SmartActionCard
                    key={action?.id}
                    action={action}
                    actionTypes={actionTypes}
                    openSidepanel={() => openSidepanel(
                      action?.actionType?.id,
                      action?.id,
                    )}
                    deleteSmartAction={deleteSmartActionNotification}
                    followUpTypes={followUpTypes}
                    isFollowUp={action?.actionType?.id === smartActionTypes.customerDown}
                  />,
                ) :
                <StyledDefault>
                  {intl.formatMessage({ id: 'ticket.smart_actions.NO_DATA_INFO' })}
                </StyledDefault>
              }
            </StyledCardDiv>
          </>,
        )}
      </Div>
    </>
  );
};
SmartActionSection.propTypes = {
  intl: PropTypes.objects,
  history: PropTypes.object,
  actions: PropTypes.array,
  actionTypes: PropTypes.array,
  deleteSuccess: PropTypes.bool,
  deleteFailure: PropTypes.bool,
  deleteSmartAction: PropTypes.func,
  getTicketActionTypes: PropTypes.func,
  objectType: PropTypes.string,
  id: PropTypes.string,
  followUpTypes: PropTypes.array,
};

export default withRouter(injectIntl(SmartActionSection));
